import { useEffect, useState, useCallback } from 'react'
import {
  apiAdminGetAdmins,
  apiAdminCreateAdmin,
  apiAdminSetAdminStatus,
  apiAdminUpdateAdminPermissions,
  ApiRequestError,
  type AdminAccount,
  type CreateAdminBody,
} from '@/lib/api'
import { PERMISSION_MENUS, type PermissionMenu, type OtpConfirmation } from '@nexbit/types'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import { useAuth } from '@/contexts/auth'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  Plus,
  RefreshCw,
  AlertCircle,
  Eye,
  EyeOff,
  X,
  Save,
  Loader2,
  ShieldOff,
  ShieldCheck,
  Crown,
  SlidersHorizontal,
} from 'lucide-react'
import { useT } from '@/contexts/language'

function statusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (s === 'active') return 'success'
  if (s === 'suspended') return 'destructive'
  return 'outline'
}

/**
 * Permission picker: super toggle, then a menu per row with its actions underneath.
 *
 * Ticking the menu grants everything in it and the actions grey out — that is genuinely
 * how the check behaves, so showing them still ticked would be a lie about what happens
 * if the menu is later unticked. Untick the menu and each action stands on its own.
 */
function PermissionPicker({
  value,
  onChange,
}: {
  value: string[] | null
  onChange: (v: string[] | null) => void
}) {
  const isSuper = value === null
  const set = new Set(value ?? [])

  function toggle(key: string, on: boolean, menu?: PermissionMenu) {
    const next = new Set(set)
    if (on) {
      next.add(key)
      // The menu key covers its actions; keeping both would just be noise in the column.
      if (menu) for (const a of menu.actions) next.delete(a.key)
    } else {
      next.delete(key)
    }
    onChange([...next])
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 font-mono text-xs cursor-pointer">
        <input
          type="checkbox"
          checked={isSuper}
          onChange={(e) => onChange(e.target.checked ? null : [])}
          className="h-3.5 w-3.5"
        />
        <Crown className="h-3.5 w-3.5 text-primary" />
        <span className="font-semibold">Super admin — ทุกเมนู + จัดการแอดมิน</span>
      </label>

      {!isSuper && (
        <div className="flex flex-col gap-1 pl-1">
          {PERMISSION_MENUS.map((menu) => {
            const wholeMenu = set.has(menu.key)
            return (
              <div key={menu.key} className="rounded-[var(--radius-sm)] border border-border/60">
                <label className="flex cursor-pointer items-center gap-2 px-2 py-1.5 font-mono text-[11px]">
                  <input
                    type="checkbox"
                    checked={wholeMenu}
                    onChange={(e) => toggle(menu.key, e.target.checked, menu)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="font-semibold">{menu.labelTh}</span>
                  {menu.actions.length > 0 && (
                    <span className="text-muted-foreground">
                      {wholeMenu ? '— ทำได้ทุกอย่างในเมนูนี้' : '— เลือกเฉพาะที่ต้องการด้านล่าง'}
                    </span>
                  )}
                </label>

                {menu.actions.length > 0 && (
                  <div className="grid gap-1 border-t border-border/50 px-2 py-1.5 sm:grid-cols-2">
                    {menu.actions.map((action) => (
                      <label
                        key={action.key}
                        title={action.descTh}
                        className={cn(
                          'flex items-start gap-1.5 font-mono text-[11px]',
                          wholeMenu ? 'cursor-not-allowed opacity-45' : 'cursor-pointer',
                        )}
                      >
                        <input
                          type="checkbox"
                          disabled={wholeMenu}
                          checked={wholeMenu || set.has(action.key)}
                          onChange={(e) => toggle(action.key, e.target.checked)}
                          className="mt-0.5 h-3.5 w-3.5"
                        />
                        <span className="min-w-0">
                          {action.labelTh}
                          {action.risk === 'money' && (
                            <span className="ml-1 rounded-full bg-destructive/10 px-1 py-px text-[9px] font-semibold text-destructive">
                              เงิน
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p className="font-mono text-xs text-destructive flex items-center gap-1" role="alert">
      <AlertCircle className="h-3 w-3 shrink-0" />
      {message}
    </p>
  )
}

// ─── Create admin ───────────────────────────────────────────────────────────────
function CreateAdminForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const t = useT()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [permissions, setPermissions] = useState<string[] | null>([])
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // A new admin account is a new way into the console, so the group approves it. The
  // body is snapshotted here because the code is bound to these exact values.
  const [pending, setPending] = useState<CreateAdminBody | null>(null)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    if (username.length < 3) return setErr('username อย่างน้อย 3 ตัว')
    if (password.length < 8) return setErr('password อย่างน้อย 8 ตัว')
    setPending({
      username,
      password,
      displayName: displayName || undefined,
      permissions,
    })
  }

  async function create(body: CreateAdminBody, otp: OtpConfirmation) {
    setSaving(true)
    try {
      await apiAdminCreateAdmin(body, otp)
      onCreated()
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-[var(--radius)] border border-border bg-card overflow-hidden mb-4"
      noValidate
    >
      <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          สร้างแอดมินใหม่
        </span>
        <button type="button" onClick={onCancel} aria-label="ยกเลิก" className="text-muted-foreground hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-4 flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="au" className="font-mono text-xs">{t.common.username}</Label>
            <Input id="au" autoComplete="off" className="font-mono text-sm" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ap" className="font-mono text-xs">{t.common.password}</Label>
            <div className="relative">
              <Input id="ap" type={showPassword ? 'text' : 'password'} autoComplete="new-password" className="font-mono text-sm pr-9" value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'ซ่อน' : 'แสดง'}>
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="an" className="font-mono text-xs">{t.common.displayName} ({t.common.optional})</Label>
          <Input id="an" className="font-mono text-sm" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="font-mono text-xs">สิทธิ์เมนู</Label>
          <PermissionPicker value={permissions} onChange={setPermissions} />
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col gap-2">
        <FieldError message={err ?? undefined} />
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1 font-mono text-xs" onClick={onCancel}>ยกเลิก</Button>
          <Button type="submit" disabled={saving} className="flex-1 font-mono text-xs font-semibold uppercase tracking-wider">
            {saving ? 'กำลังสร้าง…' : 'ถัดไป — ขอรหัสยืนยัน'}
          </Button>
        </div>
      </div>

      {pending && (
        <OtpConfirmDialog
          purpose="admin.create"
          payload={{ body: { ...pending } }}
          title="ยืนยันการสร้างแอดมิน"
          description={`สร้างบัญชี ${pending.username} · ` +
            (pending.permissions === null
              ? 'สิทธิ์ทุกเมนู (super admin)'
              : `${pending.permissions.length} เมนู`)}
          confirmLabel="สร้างแอดมิน"
          onConfirm={(otp) => create(pending, otp)}
          onDone={() => setPending(null)}
          onClose={() => setPending(null)}
        />
      )}
    </form>
  )
}

// ─── One admin row: permission summary, inline edit, suspend ────────────────────
function AdminRow({ admin, selfId, onChanged }: { admin: AdminAccount; selfId: string | undefined; onChanged: () => void }) {
  const [editing, setEditing] = useState(false)
  const [perms, setPerms] = useState<string[] | null>(admin.permissions)
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const isSelf = admin.id === selfId
  const isSuper = admin.permissions === null
  // Both row actions change who can do what in the console, so both wait for a code.
  const [pendingPerms, setPendingPerms] = useState<string[] | null | undefined>(undefined)
  const [pendingStatus, setPendingStatus] = useState<'active' | 'suspended' | null>(null)

  async function savePerms(next: string[] | null, otp: OtpConfirmation) {
    setSaving(true)
    try {
      await apiAdminUpdateAdminPermissions(admin.id, next, otp)
      setEditing(false)
      onChanged()
    } finally { setSaving(false) }
  }
  async function toggle(status: 'active' | 'suspended', otp: OtpConfirmation) {
    setToggling(true)
    try {
      await apiAdminSetAdminStatus(admin.id, status, otp)
      onChanged()
    } finally { setToggling(false) }
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-1.5">
            <p className="font-mono text-sm font-semibold">{admin.username}</p>
            {isSuper && <Crown className="h-3.5 w-3.5 text-primary" aria-label="super admin" />}
          </div>
          {admin.displayName && <p className="font-mono text-xs text-muted-foreground">{admin.displayName}</p>}
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">
          {isSuper ? 'ทุกเมนู (super)' : `${admin.permissions?.length ?? 0} เมนู`}
        </TableCell>
        <TableCell>
          <Badge variant={statusVariant(admin.status)} className="text-[10px]">{admin.status}</Badge>
        </TableCell>
        <TableCell className="text-right font-mono text-xs text-muted-foreground">
          {format(new Date(admin.createdAt), 'dd/MM/yy')}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 font-mono text-xs"
              onClick={() => { setPerms(admin.permissions); setEditing((v) => !v) }}
              disabled={isSelf}
              title={isSelf ? 'แก้สิทธิ์ตัวเองไม่ได้' : 'แก้สิทธิ์'}
            >
              <SlidersHorizontal className="h-3 w-3" /> สิทธิ์
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn('h-7 gap-1.5 font-mono text-xs', admin.status === 'active' ? 'text-destructive hover:text-destructive hover:bg-destructive/10' : 'text-success hover:text-success hover:bg-success/10')}
              onClick={() => setPendingStatus(admin.status === 'active' ? 'suspended' : 'active')}
              disabled={toggling || isSelf}
              title={isSelf ? 'ระงับตัวเองไม่ได้' : ''}
            >
              {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : admin.status === 'active' ? <ShieldOff className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
              {admin.status === 'active' ? 'ระงับ' : 'เปิด'}
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {editing && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30">
            <div className="flex flex-col gap-3 py-2">
              <PermissionPicker value={perms} onChange={setPerms} />
              <div className="flex gap-2">
                <Button size="sm" className="h-8 gap-1.5 font-mono text-xs" onClick={() => setPendingPerms(perms)} disabled={saving}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} บันทึกสิทธิ์
                </Button>
                <Button size="sm" variant="outline" className="h-8 font-mono text-xs" onClick={() => setEditing(false)}>ยกเลิก</Button>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}

      {pendingPerms !== undefined && (
        <OtpConfirmDialog
          purpose="admin.permissions.update"
          payload={{ params: { id: admin.id }, body: { permissions: pendingPerms } }}
          title="ยืนยันการแก้สิทธิ์"
          description={`แก้สิทธิ์ของ ${admin.username} เป็น ` +
            (pendingPerms === null ? 'ทุกเมนู (super admin)' : `${pendingPerms.length} เมนู`)}
          confirmLabel="บันทึกสิทธิ์"
          onConfirm={(otp) => savePerms(pendingPerms, otp)}
          onDone={() => setPendingPerms(undefined)}
          onClose={() => setPendingPerms(undefined)}
        />
      )}

      {pendingStatus && (
        <OtpConfirmDialog
          purpose="admin.status.update"
          payload={{ params: { id: admin.id }, body: { status: pendingStatus } }}
          title={pendingStatus === 'suspended' ? 'ยืนยันการระงับบัญชี' : 'ยืนยันการเปิดบัญชี'}
          description={pendingStatus === 'suspended'
            ? `ระงับบัญชี ${admin.username} — เข้าคอนโซลไม่ได้จนกว่าจะเปิดคืน`
            : `เปิดบัญชี ${admin.username} กลับมาใช้งานได้ตามสิทธิ์เดิม`}
          confirmLabel={pendingStatus === 'suspended' ? 'ระงับบัญชี' : 'เปิดบัญชี'}
          danger={pendingStatus === 'suspended'}
          onConfirm={(otp) => toggle(pendingStatus, otp)}
          onDone={() => setPendingStatus(null)}
          onClose={() => setPendingStatus(null)}
        />
      )}
    </>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────────
export default function AdminsPage() {
  const t = useT()
  const { user } = useAuth()
  const [admins, setAdmins] = useState<AdminAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setAdmins(await apiAdminGetAdmins())
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally { setLoading(false) }
  }, [t.common.error])

  useEffect(() => { void load() }, [load])

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-mono text-sm font-bold uppercase tracking-wider text-foreground">{t.nav.admins}</h1>
          <p className="font-mono text-xs text-muted-foreground mt-0.5">
            super admin เห็นทุกเมนู · แอดมินจำกัดเห็นเฉพาะเมนูที่ติ๊กให้
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading} className="font-mono text-xs gap-1.5">
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> {t.common.refresh}
          </Button>
          {!creating && (
            <Button size="sm" className="font-mono text-xs gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-3.5 w-3.5" /> สร้างแอดมิน
            </Button>
          )}
        </div>
      </div>

      {creating && <CreateAdminForm onCreated={() => { setCreating(false); void load() }} onCancel={() => setCreating(false)} />}

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">{t.nav.admins}</span>
          {!loading && <span className="font-mono text-xs text-secondary-foreground/60">{admins.length} รายการ</span>}
        </div>
        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-32" /><Skeleton className="ml-auto h-5 w-16" />
              </div>
            ))}
          </div>
        ) : admins.length === 0 ? (
          <div className="py-12 text-center"><p className="font-mono text-sm text-muted-foreground">ยังไม่มีแอดมิน</p></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>แอดมิน</TableHead>
                <TableHead>สิทธิ์</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead className="text-right">สร้างเมื่อ</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((a) => (
                <AdminRow key={a.id} admin={a} selfId={user?.id} onChanged={() => void load()} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
